import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { deleteMessage, getMessages, getThreadMessages, postMessage } from "../api/messages";
import { createChatSocket } from "../sockets/socket";
import "./PostReplies.css";

function unwrapMessages(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.messages)) return data.messages;
  if (Array.isArray(data?.replies)) return data.replies;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.messages)) return data.data.messages;
  if (Array.isArray(data?.data?.replies)) return data.data.replies;
  return [];
}

function getId(message) {
  return message.id ?? message.message_id ?? message.messageId;
}

function getParentId(message) {
  return message.parent_id ?? message.parentId;
}

function getText(message) {
  return String(message.text ?? message.content ?? message.body ?? "");
}

function getAuthorName(message) {
  const firstName = message.first_name ?? message.firstName ?? message.user?.first_name ?? message.user?.firstName;
  const lastName = message.last_name ?? message.lastName ?? message.user?.last_name ?? message.user?.lastName;
  return [firstName, lastName].filter(Boolean).join(" ") || message.username || message.user_name || "Member";
}

function sortReplies(replies) {
  return [...replies].sort((first, second) => {
    const firstTime = Date.parse(first.created_at ?? first.createdAt ?? first.time ?? "");
    const secondTime = Date.parse(second.created_at ?? second.createdAt ?? second.time ?? "");
    if (Number.isNaN(firstTime)) return Number.isNaN(secondTime) ? 0 : -1;
    if (Number.isNaN(secondTime)) return 1;
    return firstTime - secondTime;
  });
}

function upsertReply(replies, incoming) {
  const id = getId(incoming);
  if (id == null) return replies;
  const current = replies.find((reply) => String(getId(reply)) === String(id));
  const next = current
    ? replies.map((reply) => String(getId(reply)) === String(id) ? { ...reply, ...incoming } : reply)
    : [...replies, incoming];
  return sortReplies(next);
}

function upsertReplies(current, incoming) {
  return incoming.reduce(upsertReply, current);
}

function formatReplyTime(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function PostReplies({ token, canDeleteMessages = false, currentUserId }) {
  const { channelId, messageId } = useParams();
  const [parent, setParent] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const scrollRef = useRef(null);
  const socketRef = useRef(null);
  const typingTimersRef = useRef({});
  const typingStopTimerRef = useRef(null);
  const isTypingRef = useRef(false);

  useEffect(() => {
    let isActive = true;
    let isBuffering = true;
    let isJoined = false;
    let bufferedEvents = [];
    const socket = createChatSocket(token);
    socketRef.current = socket;

    function applyEvent(type, payload) {
      const message = type === "messageDeleted" ? payload?.message : payload;
      if (!message || String(message.channel_id) !== String(channelId)) return;
      if (isBuffering) {
        bufferedEvents.push({ type, payload });
        return;
      }

      if (String(getId(message)) === String(messageId)) {
        setParent((current) => ({ ...current, ...message }));
        return;
      }

      if (String(getParentId(message)) === String(messageId)) {
        setReplies((current) => upsertReply(current, message));
      }
    }

    function onTyping(payload) {
      if (String(payload?.channel_id) !== String(channelId) || payload?.user_id == null) return;
      if (currentUserId != null && String(payload.user_id) === String(currentUserId)) return;
      const userId = String(payload.user_id);
      clearTimeout(typingTimersRef.current[userId]);
      if (!payload.is_typing) {
        delete typingTimersRef.current[userId];
        setTypingUsers((current) => current.filter((id) => id !== userId));
        return;
      }
      setTypingUsers((current) => current.includes(userId) ? current : [...current, userId]);
      typingTimersRef.current[userId] = setTimeout(() => {
        delete typingTimersRef.current[userId];
        setTypingUsers((current) => current.filter((id) => id !== userId));
      }, 3000);
    }

    async function fetchHistory() {
      try {
        const [messagesResponse, threadResponse] = await Promise.all([
          getMessages(token, channelId),
          getThreadMessages(token, messageId),
        ]);
        if (!isActive) return;
        const foundParent = unwrapMessages(messagesResponse.data).find((message) => String(getId(message)) === String(messageId));
        let nextReplies = sortReplies(unwrapMessages(threadResponse.data));
        bufferedEvents.forEach(({ type, payload }) => {
          const eventMessage = type === "messageDeleted" ? payload?.message : payload;
          if (!eventMessage) return;
          if (String(getId(eventMessage)) === String(messageId)) {
            setParent((current) => ({ ...(foundParent || current || {}), ...eventMessage }));
          } else if (String(getParentId(eventMessage)) === String(messageId)) {
            nextReplies = upsertReply(nextReplies, eventMessage);
          }
        });
        bufferedEvents = [];
        isBuffering = false;
        setParent((current) => current || foundParent || null);
        setReplies(nextReplies);
        setError(foundParent ? "" : "This message could not be found in the selected channel.");
      } catch (requestError) {
        if (!isActive) return;
        setError(requestError.response?.data?.message || requestError.response?.data?.error || "Unable to load this message's replies.");
        isBuffering = false;
      } finally {
        if (isActive) setLoading(false);
      }
    }

    function onConnect() {
      isJoined = false;
      isBuffering = true;
      bufferedEvents = [];
      setLoading(true);
      socket.timeout(5000).emit("joinChannel", { channel_id: String(channelId) }, (timeoutError, result) => {
        if (!isActive) return;
        if (timeoutError || !result?.success) {
          isBuffering = false;
          setLoading(false);
          setError(timeoutError?.message || result?.error || "Unable to join this channel.");
          return;
        }
        isJoined = true;
        fetchHistory();
      });
    }

    function onConnectError(connectError) {
      if (!isActive) return;
      setLoading(false);
      setError(connectError?.message || "Unable to connect to live replies.");
    }

    function onChannelError(channelError) {
      if (!isActive) return;
      setLoading(false);
      setError(channelError?.error || channelError?.message || "Unable to join this channel.");
    }

    function onAuthenticationError(authError) {
      if (isActive) {
        setLoading(false);
        setError(authError?.message || authError?.error || "Your session is no longer valid.");
      }
      socket.disconnect();
    }

    socket.on("newThreadReply", (message) => applyEvent("newThreadReply", message));
    socket.on("newMessage", (message) => applyEvent("newMessage", message));
    socket.on("messageDeleted", (payload) => applyEvent("messageDeleted", payload));
    socket.on("typing", onTyping);
    socket.on("channelError", onChannelError);
    socket.on("authenticationError", onAuthenticationError);
    socket.on("connect_error", onConnectError);
    socket.on("connect", onConnect);
    socket.connect();

    return () => {
      isActive = false;
      isBuffering = false;
      if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
      Object.values(typingTimersRef.current).forEach(clearTimeout);
      typingTimersRef.current = {};
      if (socket.connected && isJoined && isTypingRef.current) {
        socket.emit("typing", { channel_id: String(channelId), is_typing: false });
      }
      isTypingRef.current = false;
      if (socket.connected && isJoined) socket.emit("leaveChannel", { channel_id: String(channelId) }, () => {});
      socket.off("newThreadReply");
      socket.off("newMessage");
      socket.off("messageDeleted");
      socket.off("typing", onTyping);
      socket.off("channelError", onChannelError);
      socket.off("authenticationError", onAuthenticationError);
      socket.off("connect_error", onConnectError);
      socket.off("connect", onConnect);
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
    };
  }, [token, channelId, messageId, currentUserId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [replies.length]);

  async function sendReply(event) {
    event.preventDefault();
    const text = draft.trim();
    if (!text || !token || !channelId || !messageId) return;
    setIsSending(true);
    setError("");
    try {
      await postMessage(token, { channel_id: channelId, text, parent_id: messageId });
      setDraft("");
      emitTyping(false);
      const response = await getThreadMessages(token, messageId);
      setReplies((current) => upsertReplies(current, unwrapMessages(response.data)));
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || "Unable to send this message reply.");
    } finally {
      setIsSending(false);
    }
  }

  function emitTyping(isTyping) {
    const socket = socketRef.current;
    if (!isTyping) {
      if (typingStopTimerRef.current) clearTimeout(typingStopTimerRef.current);
      typingStopTimerRef.current = null;
      if (socket?.connected && isTypingRef.current) socket.emit("typing", { channel_id: String(channelId), is_typing: false });
      isTypingRef.current = false;
      return;
    }
    if (!socket?.connected || isTypingRef.current) return;
    socket.emit("typing", { channel_id: String(channelId), is_typing: true });
    isTypingRef.current = true;
    typingStopTimerRef.current = setTimeout(() => emitTyping(false), 2500);
  }

  async function handleDeleteReply(reply) {
    if (!canDeleteMessages || getId(reply) == null || !window.confirm("Delete this reply?")) return;
    const id = getId(reply);
    setDeletingId(id);
    setError("");
    try {
      await deleteMessage(token, id);
      setReplies((current) => current.map((item) => String(getId(item)) === String(id)
        ? { ...item, text: "[Message deleted]", is_deleted: true }
        : item,
      ));
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.response?.data?.error || "Unable to delete this reply.");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <main className="post-replies-page">
      <header className="post-replies-header">
        <Link className="post-replies-back" to="/">Back to messages</Link>
        <div>
          <p className="post-replies-eyebrow">MESSAGE REPLIES</p>
          <h1>Replies</h1>
        </div>
      </header>
      {parent && (
        <section aria-label="Original message" className="post-replies-context">
          <p>{getText(parent)}</p>
          <div className="post-reply-byline">
            <strong>{getAuthorName(parent)}</strong>
            <time>{formatReplyTime(parent.created_at ?? parent.createdAt ?? parent.time)}</time>
          </div>
        </section>
      )}
      {error && <p className="post-replies-error" role="alert">{error}</p>}
      {typingUsers.length > 0 && <p className="post-replies-typing" role="status">Someone is typing...</p>}
      <div aria-label="Replies" aria-live="polite" className="post-replies-list" ref={scrollRef}>
        {loading && <p className="post-replies-state">Loading replies...</p>}
        {!loading && replies.length === 0 && <p className="post-replies-state">No replies yet.</p>}
        {replies.map((reply) => (
          <article className="post-reply-item" key={getId(reply)}>
            <p>{getText(reply)}</p>
            <div className="post-reply-byline">
              <strong>{getAuthorName(reply)}</strong>
              <time>{formatReplyTime(reply.created_at ?? reply.createdAt ?? reply.time)}</time>
              {canDeleteMessages && !reply.is_deleted && getId(reply) != null && (
                <button
                  aria-label="Delete reply"
                  className="post-reply-delete"
                  disabled={String(deletingId) === String(getId(reply))}
                  onClick={() => handleDeleteReply(reply)}
                  type="button"
                >
                  {String(deletingId) === String(getId(reply)) ? "Deleting..." : "Delete"}
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
      <form className="post-replies-composer" onSubmit={sendReply}>
        <label className="sr-only" htmlFor="post-reply-draft">Reply to message</label>
        <textarea
          id="post-reply-draft"
          onBlur={() => emitTyping(false)}
          onChange={(event) => {
            setDraft(event.target.value);
            emitTyping(Boolean(event.target.value.trim()));
          }}
          placeholder="Reply to message..."
          rows="2"
          value={draft}
        />
        <button className="send-button post-replies-submit" disabled={!draft.trim() || isSending} type="submit">
          {isSending ? "Sending..." : "Send reply"}
        </button>
      </form>
    </main>
  );
}